import { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

interface TimeSelectionProps {
  professional: any;
  appointmentTime: any;
  onTimeSelect: (time: any) => void;
}

export function TimeSelection({ professional, appointmentTime, onTimeSelect }: TimeSelectionProps) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  // Generate next 14 days
  const dates = Array.from({ length: 14 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i);
    return date;
  });

  // Standard time slots — availability is not yet checked in real-time
  const timeSlots = [
    { time: '09:00 AM', status: 'available' },
    { time: '10:00 AM', status: 'available' },
    { time: '11:00 AM', status: 'available' },
    { time: '12:00 PM', status: 'available' },
    { time: '01:00 PM', status: 'available' },
    { time: '02:00 PM', status: 'available' },
    { time: '03:00 PM', status: 'available' },
    { time: '04:00 PM', status: 'available' },
    { time: '05:00 PM', status: 'available' }
  ];

  const handleSlotSelect = (slot: string) => {
    setSelectedSlot(slot);
    onTimeSelect({
      date: selectedDate,
      time: slot
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'border-green-300 dark:border-green-800/30 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-700 dark:text-green-300';
      case 'limited':
        return 'border-yellow-300 dark:border-yellow-800/30 bg-yellow-50 dark:bg-yellow-900/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
      case 'unavailable':
        return 'border-gray-300 dark:border-white/20 bg-gray-100 dark:bg-gray-800/50 text-gray-400 dark:text-white/30 cursor-not-allowed';
      default:
        return 'border-gray-300 dark:border-white/20';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl mb-2 dark:text-white">Select Date & Time</h3>
        <p className="text-gray-600 dark:text-white/70">Choose your preferred appointment slot</p>
      </div>

      {/* Calendar Date Selection */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="dark:text-white">Select Date</h4>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-white/60">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-800/30 rounded" />
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800/30 rounded" />
              <span>Limited</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-white/20 rounded" />
              <span>Full</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {dates.slice(0, 7).map((date, index) => {
            const isSelected = selectedDate.toDateString() === date.toDateString();
            const isToday = date.toDateString() === new Date().toDateString();

            return (
              <button
                key={index}
                onClick={() => setSelectedDate(date)}
                className={`p-3 border-2 rounded-lg transition-all ${
                  isSelected
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                    : 'border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30'
                }`}
              >
                <div className="text-xs text-gray-500 dark:text-white/50 mb-1">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="text-lg dark:text-white">
                  {date.getDate()}
                </div>
                {isToday && (
                  <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">Today</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Slot Selection */}
      <div>
        <h4 className="mb-4 dark:text-white">Available Time Slots</h4>
        <div className="grid md:grid-cols-3 gap-3">
          {timeSlots.map((slot, index) => {
            const isSelected = selectedSlot === slot.time;
            const isDisabled = slot.status === 'unavailable';

            return (
              <button
                key={index}
                onClick={() => !isDisabled && handleSlotSelect(slot.time)}
                disabled={isDisabled}
                className={`p-4 border-2 rounded-lg transition-all ${
                  isSelected
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500 text-blue-700 dark:text-blue-300'
                    : getStatusColor(slot.status)
                } ${isDisabled ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{slot.time}</span>
                </div>
                {slot.status === 'limited' && !isSelected && (
                  <div className="text-xs mt-1">Few spots left</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selectedSlot && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg p-4">
          <div className="text-green-800 dark:text-green-300">
            <strong>Selected:</strong> {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} at {selectedSlot}
          </div>
        </div>
      )}

      {/* Professional Availability Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4">
        <h5 className="mb-2 dark:text-white">About {professional.name}'s Availability</h5>
        <ul className="text-sm text-gray-700 dark:text-white/70 space-y-1">
          <li>• Real-time availability updated</li>
          <li>• Buffer times automatically handled</li>
          <li>• Instant confirmation upon booking</li>
          <li>• Free cancellation up to 24 hours before</li>
        </ul>
      </div>
    </div>
  );
}